import React, { useState, useEffect, useRef, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useFirestore } from "../../firebase/FirestoreContext";
import { getDoc, doc, collection, getDocs, updateDoc, deleteDoc, writeBatch, addDoc, deleteField } from "firebase/firestore";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import config from "../../../config";
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from "@mui/material";
import { TableSortLabel } from "@mui/material";
import { SortableContainer, SortableElement } from "react-sortable-hoc";
import { arrayMoveImmutable } from "array-move";
import { EnvContext } from "../../../context/EnvContext";
import { CSVLink } from "react-csv";
import { Star, WarningAmber } from "@mui/icons-material";
import ReactQuill, { Quill } from "react-quill";

const EditProduct = () => {
  const environment = useContext(EnvContext);
  const db = useFirestore();
  const { id } = useParams();
  const navigate = useNavigate();

  // Determine the base path based on the environment
  const collectionPath = environment === "staging" ? config.enveironment.staging.collectionPath : config.enveironment.production.collectionPath;

  const [sortConfig, setSortConfig] = useState({
    key: "product_name",
    direction: "asc",
  });

  const [brands, setBrands] = useState([]);
  const [dosingGuides, setDosingGuides] = useState([]);
  const [products, setProducts] = useState([]);
  const [productData, setProductData] = useState({
    product_uid: "",
    brand_uid: "",
    product_name: "",
    product_description: "",
    potency_value: "0",
    key_ingredients: "",
    tac: "",
    weight_units: "",
    net_weight: "0",
    product_category: "",
    effects: "",
    strength: "",
    product_type: "",
    number_servings: 0,
    serving_size: "",
    dose_per_serving: 0,
    dosing_guide: "",
    images: [],
    perishable: false,
    inactive: false,
    rating_initial: {
      rating: 0,
      voting: 0,
    },
    tags: [],
    subcategories: [],
    vibes: [],
    staff_pick: false,
    popular_product: false,
    order_category_row: 0,
    order_category_page: 0,
  });
  const [loading, setLoading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [currentTab, setCurrentTab] = useState("form");
  const [files, setFiles] = useState([]);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  //
  const [locations, setLocations] = useState([]);
  const [selectedCompliance, setSelectedCompliance] = useState(productData?.compliance || []);

  //
  const [subcategories, setSubcategories] = useState([]);
  const [vibesList, setVibesList] = useState([]);
  const [selectedVibes, setSelectedVibes] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [selectedSubcategory, setSelectedSubcategory] = useState([]);
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);

  const [reviews, setReviews] = useState([]);

  const fileInputRef = useRef(null);

  //
  const effectsOptions = config.effectsOptions;
  const productTypeOptions = config.productTypeOptions;
  const productCategoryOptions = config.productCategoryOptions;
  const weightUnitsOptions = config.weightUnitsOptions;
  const strain_typeOptions = config.strain_typeOptions;

  //
  const [tags, setTags] = useState([]);

  useEffect(() => {
    if (id) {
      const productDocRef = doc(db, `${collectionPath}Products/ProductsList/DataBase`, id);

      getDoc(productDocRef)
        .then((docSnap) => {
          if (docSnap.exists()) {
            const data = docSnap.data();
            setProductData(data);

            setSelectedTags(data.tags || []);
            setSelectedSubcategory(data.subcategories || []);
            setSelectedVibes(data.vibes || []);
          } else {
            console.error("No such product document!");
          }
        })
        .catch((error) => {
          console.error("Error fetching product:", error);
        });
    } else {
      const productsCollectionRef = collection(db, `${collectionPath}Products/ProductsList/DataBase`);

      getDocs(productsCollectionRef)
        .then((querySnapshot) => {
          const productsArray = [];
          querySnapshot.forEach((doc) => {
            productsArray.push({ id: doc.id, ...doc.data() });
          });
          setProducts(productsArray);
        })
        .catch((error) => {
          console.error("Error fetching products:", error);
        });
    }

    const fetchBrands = async () => {
      const brandsCollectionRef = collection(db, `${collectionPath}Brands/BrandsList/DataBase`);
      const querySnapshot = await getDocs(brandsCollectionRef);
      const brandsArray = [];
      querySnapshot.forEach((doc) => {
        brandsArray.push({ id: doc.id, ...doc.data() });
      });
      setBrands(brandsArray);
    };

    const fetchDosingGuides = async () => {
      const dosingGuidesCollectionRef = collection(db, `${collectionPath}DosingGuides/DosingGuidesList/DataBase`);
      const querySnapshot = await getDocs(dosingGuidesCollectionRef);
      const guidesArray = [];
      querySnapshot.forEach((doc) => {
        guidesArray.push(doc.data().name);
      });
      setDosingGuides(guidesArray);
    };

    fetchBrands();
    fetchDosingGuides();
  }, [db, id, collectionPath]);

  //
  useEffect(() => {
    // Fetch products on component mount
    const fetchProducts = async () => {
      try {
        const productsCollectionRef = collection(db, `${collectionPath}Products/ProductsList/DataBase`);
        const productsSnapshot = await getDocs(productsCollectionRef);

        // Step 1: Build a local product map
        const productsMap = {};
        const productsArray = productsSnapshot.docs.map((doc) => {
          const productData = { id: doc.id, ...doc.data(), sold_count: 0 }; // Initialize sold_count to 0
          productsMap[doc.id] = productData;
          return productData;
        });

        // Step 2: Fetch all orders and calculate sold counts
        const ordersCollectionRef = collection(db, `${collectionPath}Orders/OrdersList/DataBase`);
        const ordersSnapshot = await getDocs(ordersCollectionRef);

        ordersSnapshot.docs.forEach((orderDoc) => {
          const orderData = orderDoc.data();
          if (Array.isArray(orderData.items)) {
            orderData.items.forEach((item) => {
              if (item.product_id && productsMap[item.product_id]) {
                productsMap[item.product_id].sold_count += item.quantity || 0; // Increment sold_count
              }
            });
          }
        });

        // Step 3: Update the products state with sold counts
        setProducts(Object.values(productsMap)); // Convert the map back to an array
      } catch (error) {
        console.error("Error fetching products:", error);
      }
    };

    fetchProducts();
  }, [db, collectionPath]);

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);

    if (!productData.product_uid || !productData.brand_uid || !productData.product_name) {
      alert("Please fill out all required fields");
      setLoading(false);
      return;
    }

    const productDocRef = doc(db, `${collectionPath}Products/ProductsList/DataBase`, id);
    console.log(`${collectionPath}Products/ProductsList/DataBase`, id)

    const selectedBrand = brands.find((brand) => brand.brand_uid === Number(productData.brand_uid));

    const validSubcategories = selectedSubcategory.filter((sub) => subcategories.includes(sub));

    const updatedProduct = {
      product_uid: productData.product_uid,
      brand_uid: productData.brand_uid,
      brand_name: selectedBrand ? selectedBrand.brand_name : "",
      product_name: productData.product_name,
      product_description: productData.product_description || "",
      potency_value: Number(productData.potency_value) || 0,
      key_ingredients: productData.key_ingredients || "",
      tac: productData.tac || "",
      net_weight: productData.net_weight || "0",
      weight_units: productData.weight_units || "",
      product_category: productData.product_category || "",
      strain_type: productData.strain_type || "",
      effects: productData.effects || "",
      strength: productData.strength || "",
      product_type: productData.product_type || "",
      number_servings: productData.number_servings || 0,
      serving_size: productData.serving_size || "",
      dosing_guide: productData.dosing_guide || "",
      dose_per_serving: productData.dose_per_serving || 0,
      images: productData.images || [],
      perishable: productData.perishable,
      inactive: productData.inactive || false,
      rating_initial: {
        rating: productData?.rating_initial?.rating || 0,
        voting: productData?.rating_initial?.voting || 0,
      },
      tags: selectedTags,
      subcategories: validSubcategories,
      vibes: selectedVibes,
      staff_pick: productData.staff_pick || false,
      popular_product: productData.popular_product || false,
      order_category_row: Number(productData.order_category_row) || Number(0),
      order_category_page: Number(productData.order_category_page) || Number(0),
    };

    updateDoc(productDocRef, updatedProduct)
      .then(async () => {
        toast.success("Changes saved successfully");

        // Reload the product data to ensure state consistency
        getDoc(productDocRef)
          .then((docSnap) => {
            if (docSnap.exists()) {
              const freshData = docSnap.data();
              setProductData(freshData);
              setSelectedSubcategory(freshData.subcategories || []);
              setSelectedTags(freshData.tags || []);
              setSelectedVibes(freshData.vibes || []);
            }
          })
          .catch((error) => {
            console.error("Error reloading product data:", error);
          });
      })
      .catch((error) => {
        toast.error("Error saving changes");
        console.error("Error updating product:", error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const formatUID = (uid, totalLength = 8) => {
    return String(uid).padStart(totalLength, "0");
  };

  //
  const handleDeleteProduct = async () => {
    setLoading(true);
    try {
      // Step 1: Delete product from the Products collection
      const productDocRef = doc(db, `${collectionPath}Products/ProductsList/DataBase`, id);

      // Step 2: Find all listings that match the product_id
      const listingsCollectionRef = collection(db, `${collectionPath}Listings/ListingsList/DataBase`);
      const querySnapshot = await getDocs(listingsCollectionRef);

      // ** Batch Initialization using writeBatch(db) **
      const batch = writeBatch(db); // Use writeBatch in Firestore v9+

      querySnapshot.forEach((docSnapshot) => {
        const listing = docSnapshot.data();
        if (listing.product_id === id) {
          // Assuming `id` is the product_id in listings
          const listingDocRef = doc(db, `${collectionPath}Listings/ListingsList/DataBase`, docSnapshot.id);
          batch.delete(listingDocRef); // Add each deletion to the batch
        }
      });

      // Commit batch deletion of all related listings
      await batch.commit();

      // Step 3: Delete the product itself
      await deleteDoc(productDocRef);

      toast.success("Product and associated listings deleted successfully");
      navigate(`/main/products/edit`);
    } catch (error) {
      console.error("Error details:", error); // Log the full error for debugging
      toast.error("Error deleting product and listings");
    } finally {
      setShowDeleteDialog(false);
      setLoading(false);
    }
  };

  const renderDeleteDialog = () => (
    <Dialog open={showDeleteDialog} onClose={() => setShowDeleteDialog(false)}>
      <DialogTitle>Confirm Delete</DialogTitle>
      <DialogContent>
        <DialogContentText>Are you sure you want to delete this product? This action cannot be undone.</DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setShowDeleteDialog(false)} color="primary">
          No
        </Button>
        <Button onClick={handleDeleteProduct} color="error">
          Yes
        </Button>
      </DialogActions>
    </Dialog>
  );

  useEffect(() => {
    const fetchTags = async () => {
      const tagsCollectionRef = collection(db, `${collectionPath}SooperstockSettings/ProductTags/DataBase`);
      const querySnapshot = await getDocs(tagsCollectionRef);

      const tagsArray = [];
      querySnapshot.forEach((doc) => {
        tagsArray.push(doc.data().name);
      });
      setTags(tagsArray);
    };
    fetchTags();
  }, [productData.product_category, db, collectionPath]);

  // Get Vibes

  useEffect(() => {
    const fetchVibes = async () => {
      const vibesCollectionRef = collection(db, `${collectionPath}SooperstockSettings/ProductVibes/DataBase`);
      const querySnapshot = await getDocs(vibesCollectionRef);

      const vibesArray = [];
      querySnapshot.forEach((doc) => {
        vibesArray.push(doc.data().name);
      });
      setVibesList(vibesArray);
    };
    fetchVibes();
  }, [db, collectionPath, id]);

  //
  //

  useEffect(() => {
    if (productData.product_category) {
      const fetchSubcategories = async () => {
        try {
          const subcategoriesCollectionRef = collection(db, `${collectionPath}SooperstockSettings/ProductCategories/DataBase`);
          const querySnapshot = await getDocs(subcategoriesCollectionRef);
          const categoryDoc = querySnapshot.docs.find((doc) => doc.data().name === productData.product_category);

          if (categoryDoc) {
            const newSubcategories = categoryDoc.data().subCategories || [];
            setSubcategories(newSubcategories);

            // Filter the selected subcategories to only include valid ones for this category
            const validSelected = productData.subcategories?.filter((sub) => newSubcategories.includes(sub)) || [];
            setSelectedSubcategory(validSelected);
          } else {
            setSubcategories([]);
            setSelectedSubcategory([]); // Clear selections when no subcategories
          }
        } catch (error) {
          console.error("Error fetching subcategories:", error);
          setSubcategories([]);
          setSelectedSubcategory([]);
        }
      };

      fetchSubcategories();
    }
  }, [productData.product_category, productData.subcategories, db, collectionPath]);

  const handleSubcategoryToggle = (subcategory) => {
    setSelectedSubcategory((prevSubcategory) =>
      prevSubcategory.includes(subcategory) ? prevSubcategory.filter((t) => t !== subcategory) : [...prevSubcategory, subcategory]
    );
  };

  const handleTagToggle = (tag) => {
    setSelectedTags((prevTags) => (prevTags.includes(tag) ? prevTags.filter((t) => t !== tag) : [...prevTags, tag]));
  };

  const handleVibesToggle = (vibe) => {
    setSelectedVibes((prevVibes) => (prevVibes.includes(vibe) ? prevVibes.filter((v) => v !== vibe) : [...prevVibes, vibe]));
  };

  //
  const getNewProductUID = async () => {
    const productsCollectionRef = collection(db, `${collectionPath}Products/ProductsList/DataBase`);
    const productsSnapshot = await getDocs(productsCollectionRef);
    let maxUID = 0;
    productsSnapshot.forEach((doc) => {
      const data = doc.data();
      const uid = parseInt(data.product_uid, 10);
      if (uid > maxUID) {
        maxUID = uid;
      }
    });
    return maxUID + 1;
  };

  const handleDuplicateProduct = async () => {
    if (!productData.product_name) {
      alert("No product data available to duplicate.");
      return;
    }

    setLoading(true);

    try {
      // Create a new product data object with the same properties as the current one
      const duplicatedProduct = {
        ...productData,
        product_name: `COPY of ${productData.product_name}`, // Change the name to "COPY of Product X"
        product_uid: await getNewProductUID(), // Make sure the new product has a unique UID
      };

      // Get a reference to the collection where the product will be added
      const productsCollectionRef = collection(db, `${collectionPath}Products/ProductsList/DataBase`);

      // Add the duplicated product to the Firestore collection
      const newProductRef = await addDoc(productsCollectionRef, duplicatedProduct);

      // Redirect to the edit page of the newly duplicated product
      navigate(`/main/products/edit/${newProductRef.id}`);
      await new Promise((resolve) => setTimeout(resolve, 1000));
      toast.success(`Product duplicated successfully! New product ID: ${newProductRef.id}`);
    } catch (error) {
      toast.error("Error duplicating product.");
      console.error("Error duplicating product:", error);
    } finally {
      setLoading(false);
    }
  };

  if (id) {
    return (
      <div>
        {renderDeleteDialog()}

        <ToastContainer
          position="top-center"
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
        />

        {currentTab === "form" && (
          <form onSubmit={handleSubmit} style={styles.form}>
            <div style={{ padding: "20px" }}>
              <div style={styles.formElement}>
                <label style={styles.formLabel}>Product UID:</label>
                <input
                  type="text"
                  value={`P${formatUID(productData.product_uid)}`}
                  onChange={(e) =>
                    setProductData({
                      ...productData,
                      product_uid: e.target.value,
                    })
                  }
                  style={styles.formInput}
                  disabled
                />
              </div>
              <div style={styles.formElement}>
                <label style={styles.formLabel}>Brand&#42;:</label>
                <select
                  value={productData.brand_uid}
                  onChange={(e) =>
                    setProductData({
                      ...productData,
                      brand_uid: e.target.value,
                    })
                  }
                  style={styles.formSelect}
                >
                  <option key={""} value={""}></option>
                  {brands
                    .slice()
                    .sort((a, b) => a.brand_name.localeCompare(b.brand_name)) // Sort brands alphabetically
                    .map((brand) => (
                      <option key={brand.brand_uid} value={brand.brand_uid}>
                        {brand.brand_name}
                      </option>
                    ))}
                </select>
              </div>
              <div style={styles.formElement}>
                <label style={styles.formLabel}>Name&#42;:</label>
                <input
                  type="text"
                  value={productData.product_name}
                  onChange={(e) =>
                    setProductData({
                      ...productData,
                      product_name: e.target.value,
                    })
                  }
                  style={styles.formInput}
                />
              </div>
              <div style={styles.formElement}>
                <label style={styles.formLabel}>Description:</label>
                <ReactQuill
                  theme="snow"
                  value={productData.product_description || ""}
                  onChange={(value) =>
                    setProductData((prevData) => ({
                      ...prevData,
                      product_description: value,
                    }))
                  }
                  style={{ height: 200, marginBottom: 50 }}
                />
              </div>
              <div style={styles.formElement}>
                <label style={styles.formLabel}>Strain Type:</label>
                <select
                  value={productData.strain_type}
                  onChange={(e) =>
                    setProductData({
                      ...productData,
                      strain_type: e.target.value,
                    })
                  }
                  style={styles.formSelect}
                >
                  <option value=""></option>
                  {strain_typeOptions.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>
              <div style={styles.formElement}>
                <label style={styles.formLabel}>Effects:</label>
                <select value={productData.effects} onChange={(e) => setProductData({ ...productData, effects: e.target.value })} style={styles.formSelect}>
                  <option value=""></option>
                  {effectsOptions.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>
              <div style={styles.formElement}>
                <label style={styles.formLabel}>Strength:</label>
                <input
                  type="text"
                  value={productData.strength}
                  onChange={(e) => setProductData({ ...productData, strength: e.target.value })}
                  style={styles.formInput}
                />
              </div>
              <div style={styles.formElement}>
                <label style={styles.formLabel}>Product Type:</label>
                <select
                  value={productData.product_type}
                  onChange={(e) =>
                    setProductData({
                      ...productData,
                      product_type: e.target.value,
                    })
                  }
                  style={styles.formSelect}
                >
                  <option value=""></option>
                  {productTypeOptions.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>
              <div style={styles.formElement}>
                <label style={styles.formLabel}>Product Category:</label>
                <select
                  value={productData.product_category}
                  onChange={(e) =>
                    setProductData({
                      ...productData,
                      product_category: e.target.value,
                    })
                  }
                  style={styles.formSelect}
                >
                  <option value=""></option>
                  {productCategoryOptions.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>
              {/* Subcategories */}
              <div style={styles.formElement}>
                <label style={styles.formLabel}>Product subcategories:</label>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "10px", marginBottom: "10px" }}>
                  {subcategories ? (
                    subcategories.map((subcategory, index) => (
                      <span
                        key={index}
                        onClick={() => handleSubcategoryToggle(subcategory)}
                        style={{
                          padding: "6px 12px",
                          borderRadius: "25px",
                          display: "inline-block",
                          fontSize: 12,
                          cursor: "pointer",
                          backgroundColor: selectedSubcategory.includes(subcategory) ? "#478169" : "#ccc",
                          color: selectedSubcategory.includes(subcategory) ? "white" : "black",
                        }}
                      >
                        {subcategory}
                      </span>
                    ))
                  ) : (
                    <span style={{ opacity: 0.5 }}>No subcategories available for this category</span>
                  )}
                </div>
              </div>
              {/* tags */}
              <div style={styles.formElement}>
                <label style={styles.formLabel}>Product tags:</label>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "10px", marginBottom: "10px" }}>
                  {tags ? (
                    tags.map((tag, index) => (
                      <span
                        key={index}
                        onClick={() => handleTagToggle(tag)}
                        style={{
                          padding: "6px 12px",
                          borderRadius: "25px",
                          display: "inline-block",
                          fontSize: 12,
                          cursor: "pointer",
                          backgroundColor: selectedTags.includes(tag) ? "#478169" : "#ccc",
                          color: selectedTags.includes(tag) ? "white" : "black",
                        }}
                      >
                        {tag}
                      </span>
                    ))
                  ) : (
                    <span style={{ opacity: 0.5 }}>No tags available for this category</span>
                  )}
                </div>
              </div>
              {/* vibes */}
              <div style={styles.formElement}>
                <label style={styles.formLabel}>Product vibes:</label>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "10px", marginBottom: "10px" }}>
                  {vibesList ? (
                    vibesList.map((vibe, index) => (
                      <span
                        key={index}
                        onClick={() => handleVibesToggle(vibe)}
                        style={{
                          padding: "6px 12px",
                          borderRadius: "25px",
                          display: "inline-block",
                          fontSize: 12,
                          cursor: "pointer",
                          backgroundColor: selectedVibes.includes(vibe) ? "#478169" : "#ccc",
                          color: selectedVibes.includes(vibe) ? "white" : "black",
                        }}
                      >
                        {vibe}
                      </span>
                    ))
                  ) : (
                    <span style={{ opacity: 0.5 }}>No tags available for this category</span>
                  )}
                </div>
              </div>
              <div style={styles.formElement}>
                <label style={styles.formLabel}>Dosing Guide:</label>
                <select
                  value={productData.dosing_guide}
                  onChange={(e) =>
                    setProductData({
                      ...productData,
                      dosing_guide: e.target.value,
                    })
                  }
                  style={styles.formSelect}
                >
                  <option value=""></option>
                  {dosingGuides.map((guide) => (
                    <option key={guide} value={guide}>
                      {guide}
                    </option>
                  ))}
                </select>
              </div>
              <div style={{ display: "flex", flexDirection: "row" }}>
                <div style={{ ...styles.formElement, marginRight: 20 }}>
                  <label style={styles.formLabel}>Weight Value:</label>
                  <input
                    type="text"
                    value={productData.net_weight}
                    onChange={(e) =>
                      setProductData({
                        ...productData,
                        net_weight: e.target.value,
                      })
                    }
                    style={styles.formInput}
                  />
                </div>
                <div style={styles.formElement}>
                  <label style={styles.formLabel}>Weight Unit:</label>
                  <select
                    value={productData.weight_units}
                    onChange={(e) =>
                      setProductData({
                        ...productData,
                        weight_units: e.target.value,
                      })
                    }
                    style={styles.formSelect}
                  >
                    <option value=""></option>
                    {weightUnitsOptions.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div style={styles.formElement}>
                <label style={styles.formLabel}>Potency:</label>
                <input
                  type="text"
                  value={productData.potency_value}
                  onChange={(e) =>
                    setProductData({
                      ...productData,
                      potency_value: e.target.value,
                    })
                  }
                  style={styles.formInput}
                />
              </div>
              <div style={styles.formElement}>
                <label style={styles.formLabel}>Key Ingredients:</label>
                <input
                  type="text"
                  value={productData.key_ingredients}
                  onChange={(e) =>
                    setProductData({
                      ...productData,
                      key_ingredients: e.target.value,
                    })
                  }
                  style={styles.formInput}
                />
              </div>
              {selectedTags.some(tag => String(tag).toLowerCase() === "thc") && (
                <div style={styles.formElement}>
                  <label style={styles.formLabel}>TAC:</label>
                  <input
                    type="text"
                    value={productData.tac}
                    onChange={(e) =>
                      setProductData({
                        ...productData,
                        tac: e.target.value,
                      })
                    }
                    style={styles.formInput}
                  />
                </div>
              )}
              <div style={styles.formElement}>
                <label style={styles.formLabel}>Number of Servings:</label>
                <input
                  type="text"
                  value={productData.number_servings}
                  onChange={(e) =>
                    setProductData({
                      ...productData,
                      number_servings: e.target.value,
                    })
                  }
                  style={styles.formInput}
                />
              </div>
              <div style={styles.formElement}>
                <label style={styles.formLabel}>Serving Size:</label>
                <input
                  type="text"
                  value={productData.serving_size}
                  onChange={(e) =>
                    setProductData({
                      ...productData,
                      serving_size: e.target.value,
                    })
                  }
                  style={styles.formInput}
                />
              </div>
              <div style={styles.formElement}>
                <label style={styles.formLabel}>Dose per Serving:</label>
                <input
                  type="text"
                  value={productData.dose_per_serving}
                  onChange={(e) =>
                    setProductData({
                      ...productData,
                      dose_per_serving: e.target.value,
                    })
                  }
                  style={styles.formInput}
                />
              </div>
              <div style={{ ...styles.formElement, display: "flex", flexDirection: "row" }}>
                <label style={styles.formLabel}>Perishable:</label>
                <input
                  type="checkbox"
                  checked={productData.perishable}
                  onChange={(e) =>
                    setProductData({
                      ...productData,
                      perishable: e.target.checked,
                    })
                  }
                  style={{ ...styles.formInput, marginLeft: 5 }}
                />
              </div>

              {/** SORTING */}
              <div style={{ display: "flex", flexDirection: "row" }}>
                <div style={{ ...styles.formElement, marginRight: 20 }}>
                  <label style={styles.formLabel}>Order Category row:</label>
                  <input
                    type="number"
                    value={productData?.order_category_row || 0}
                    onChange={(e) =>
                      setProductData({
                        ...productData,
                        order_category_row: e.target.value || 0,
                      })
                    }
                    style={{ ...styles.formInput, width: "100px" }}
                  />
                </div>
                <div style={styles.formElement}>
                  <label style={styles.formLabel}>Order Category Page:</label>
                  <input
                    type="number"
                    value={productData?.order_category_page || 0}
                    onChange={(e) =>
                      setProductData({
                        ...productData,
                        order_category_page: e.target.value || 0,
                      })
                    }
                    style={{ ...styles.formInput, width: "100px" }}
                  />
                </div>
              </div>
              <div style={{ ...styles.formElement, display: "flex", flexDirection: "row" }}>
                <label style={styles.formLabel}>Popular product:</label>
                <input
                  type="checkbox"
                  checked={productData.popular_product || false}
                  onChange={(e) =>
                    setProductData({
                      ...productData,
                      popular_product: e.target.checked,
                    })
                  }
                  style={{ ...styles.formInput, marginLeft: 5 }}
                />
              </div>
              <div style={{ ...styles.formElement, display: "flex", flexDirection: "row" }}>
                <label style={styles.formLabel}>Staff pick:</label>
                <input
                  type="checkbox"
                  checked={productData.staff_pick || false}
                  onChange={(e) =>
                    setProductData({
                      ...productData,
                      staff_pick: e.target.checked,
                    })
                  }
                  style={{ ...styles.formInput, marginLeft: 5 }}
                />
              </div>
              <div style={{ ...styles.formElement, display: "flex", flexDirection: "row" }}>
                <label style={styles.formLabel}>Inactive:</label>
                <input
                  type="checkbox"
                  checked={productData.inactive || false}
                  onChange={(e) =>
                    setProductData({
                      ...productData,
                      inactive: e.target.checked,
                    })
                  }
                  style={{ ...styles.formInput, marginLeft: 5 }}
                />
              </div>

              {/** RATINGS */}
              <div style={{ display: "flex", flexDirection: "row" }}>
                <div style={{ ...styles.formElement, marginRight: 20 }}>
                  <label style={styles.formLabel}>Rating:</label>
                  <input
                    type="number"
                    value={productData?.rating_initial?.rating}
                    onChange={(e) =>
                      setProductData({
                        ...productData,
                        rating_initial: {
                          ...productData?.rating_initial,
                          rating: parseFloat(e.target.value) || 0,
                        },
                      })
                    }
                    style={{ ...styles.formInput, width: "100px" }}
                    min="0"
                    max="5"
                    step="0.1"
                  />
                </div>
                <div style={styles.formElement}>
                  <label style={styles.formLabel}>Voting:</label>
                  <input
                    type="number"
                    value={productData?.rating_initial?.voting}
                    onChange={(e) =>
                      setProductData({
                        ...productData,
                        rating_initial: {
                          ...productData?.rating_initial,
                          voting: parseInt(e.target.value) || 0,
                        },
                      })
                    }
                    style={{ ...styles.formInput, width: "100px" }}
                    min="0"
                  />
                </div>
              </div>

              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginTop: "20px",
                }}
              >
                <div>
                  <button
                    type="submit"
                    style={{
                      ...styles.button,
                      backgroundColor: loading ? "silver" : "#478169",
                    }}
                  >
                    {loading ? "Submitting..." : "Submit"}
                  </button>
                  <button
                    type="button"
                    onClick={handleDuplicateProduct}
                    style={{
                      ...styles.button,
                      backgroundColor: "transparent",
                      color: "black",
                      width: "150px",
                      marginLeft: "8px",
                      border: "1px solid gray",
                    }}
                    variant="outlined"
                  >
                    {loading ? "Duplicating..." : "Duplicate Product"}
                  </button>
                  <button
                    type="button"
                    onClick={() => window.location.reload()} // Reload the page
                    style={{
                      ...styles.button,
                      backgroundColor: "transparent",
                      color: "black",
                      width: "100px",
                      marginLeft: "8px",
                      border: "1px solid gray",
                    }}
                  >
                    Cancel
                  </button>
                </div>
                <button
                  type="button"
                  onClick={() => setShowDeleteDialog(true)}
                  style={{
                    ...styles.button,
                    backgroundColor: "red",
                    color: "white",
                  }}
                >
                  Delete Product
                </button>
              </div>
            </div>
          </form>
        )}
      </div>
    );
  }
};

const styles = {
  tabButton: {
    padding: "10px",
    backgroundColor: "white",
    cursor: "pointer",
    marginRight: 5,
    borderRadius: "5px 5px 0 0",
    fontSize: 12,
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
    width: 300,
    border: "1px solid #ccc",
    flex: 1,
    borderRadius: 5,
    backgroundColor: "#f9f9f9",
    width: "100%",
  },
  formElement: {
    display: "flex",
    flexDirection: "column",
    gap: 0,
    marginBottom: 10,
  },
  formLabel: {
    marginBottom: 5,
    fontSize: 12,
    textTransform: "uppercase",
    color: "#4a4141",
  },
  formInput: {
    padding: "10px 15px",
    marginBottom: 10,
    fontSize: 16,
    border: ".5px solid #ccc",
    borderRadius: 5,
  },
  formSelect: {
    fontSize: 16,
    border: "0.5px solid #ccc",
    borderRadius: 5,
    height: 38,
    backgroundColor: "#fff",
  },
  button: {
    padding: "0px",
    width: "120px",
    backgroundColor: "#478169",
    color: "white",
    border: "none",
    borderRadius: 5,
    cursor: "pointer",
    fontSize: 14,
    height: 50,
  },
  table: {
    borderCollapse: "collapse",
    width: "100%",
  },
  th: {
    border: "1px solid #d1d0d0",
    padding: "8px",
    textAlign: "left",
  },
  td: {
    border: "1px solid #d1d0d0",
    padding: "8px",
    color: "gray",
  },
  tdName: {
    border: "1px solid #d1d0d0",
    padding: "8px",
    cursor: "pointer",
  },
  clickableRow: {
    cursor: "pointer",
  },
};

export default EditProduct;
